import React, { useState } from "react"
import moment from "moment"
import CustomModal from "../basics/interface/CustomModal"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import { filterEmployesActifs } from "../../utilities/employes"
import { filtrerMoyensPaiement } from "../../hooks/moyenPaiement"
import {
	PAYEUR_CLIENT,
	PAYEURS_RESTITUTION,
	fraisHorsHeures,
	joursGardiennage,
	periodesGardiennage,
} from "./livrerRestituer"

// « Livrer / Restituer » de la fiche dépannage (Jordan 30.10, lot 7) —
// remplace RestiModal et le bouton « Créer une relivraison ». Véhicule dans
// un de nos parcs : « Restituer » (repris par, date et heure modifiables,
// gardiennage calculé jusqu'à cette date, frais hors heures de la
// configuration, encaissement) ou « Livrer » (création de la livraison du
// dispatch, DepLivraisonMissionModal). Le formulaire n'est pas sauvé : la
// fiche devient modifiée (la quittance suit le save, comme avant).
// Lot 10 (D2, Jordan 30.10) : `messageSuite` (« Restitution saisie :
// enregistrez puis passez au contrôle ») rappelle les deux étapes, qui
// restent séparées (rien n'est enchaîné).

const ENCAISSEMENTS = [
	["paye", "Payé maintenant"],
	["rien", "Rien à payer"],
	["autrement", "Réglé autrement"],
]

const round2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100
const chf = (v) => `${round2(v).toFixed(2)} CHF`

const ms = {
	pills: { display: "flex", gap: 6, flexWrap: "wrap" },
	pill: (actif) => ({
		padding: "6px 14px",
		borderRadius: 99,
		border: `1px solid ${actif ? COLORS.accent : COLORS.border}`,
		backgroundColor: actif ? COLORS.accentLight : COLORS.surface,
		color: actif ? COLORS.accent : COLORS.textMuted,
		fontSize: 13,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
	ligne: { display: "flex", gap: 8, alignItems: "center" },
	aide: { fontSize: 12, color: COLORS.textMuted, marginTop: 4 },
	periode: { fontSize: 12, color: COLORS.text, marginTop: 2 },
	suite: {
		fontSize: 13,
		fontWeight: 600,
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
		border: `1px solid ${COLORS.accent}`,
		borderRadius: 8,
		padding: "8px 12px",
		marginBottom: 16,
	},
	total: {
		fontSize: 13,
		color: COLORS.text,
		fontWeight: 600,
		marginTop: 8,
	},
}

const DepLivrerRestituerModalV2 = ({
	dep,
	restePayer,
	employes,
	moyenPaiements,
	configFrais,
	employeId,
	peutLivrer,
	messageSuite,
	onRestituer,
	onLivrer,
	onClose,
}) => {
	const maintenant = moment()
	const [mode, setMode] = useState("restituer")
	const [reprisPar, setReprisPar] = useState("")
	const [date, setDate] = useState(maintenant.format("YYYY-MM-DD"))
	const [heure, setHeure] = useState(maintenant.format("HH:mm"))
	const quand = () => moment(date + " " + heure, "YYYY-MM-DD HH:mm")
	const joursCalcules = (d, h) =>
		joursGardiennage(dep, moment(d + " " + h, "YYYY-MM-DD HH:mm"))
	const [gardQ, setGardQ] = useState(joursCalcules(date, heure))
	const [gardT, setGardT] = useState(
		parseFloat(dep.gardT) || (joursCalcules(date, heure) > 0 ? 40 : 0),
	)
	const [frais, setFrais] = useState(fraisHorsHeures(configFrais, maintenant))
	const [encaissement, setEncaissement] = useState("paye")
	const [moyenPaie, setMoyenPaie] = useState(0)
	const [caissier, setCaissier] = useState(employeId || 0)
	const [payeur, setPayeur] = useState(PAYEUR_CLIENT)

	// Date / heure changées : gardiennage et frais recalculés (la saisie
	// manuelle reste possible ensuite).
	const changerQuand = (d, h) => {
		setDate(d)
		setHeure(h)
		const q = moment(d + " " + h, "YYYY-MM-DD HH:mm")
		if (!q.isValid()) return
		setGardQ(joursCalcules(d, h))
		setFrais(fraisHorsHeures(configFrais, q))
	}

	// Détail du calcul (Jordan 30.10) : une ligne par séjour au parc,
	// recalculé à chaque changement de date ou d'heure.
	const periodes = quand().isValid() ? periodesGardiennage(dep, quand()) : []

	const gardTot = round2((parseInt(gardQ) || 0) * round2(gardT))
	// Reste à payer après la restitution : écart de gardiennage et de frais
	// par rapport à la fiche (computeTotalFinal = … + gardTot + restiTot).
	const montant = round2(
		(parseFloat(restePayer) || 0) +
			(gardTot - round2(dep.gardTot)) +
			(round2(frais) - round2(dep.restiTot)),
	)
	const valide =
		quand().isValid() &&
		(encaissement !== "paye" ||
			montant <= 0 ||
			(Number(moyenPaie) > 0 && Number(caissier) > 0))

	const restituer = () =>
		onRestituer({
			reprisPar: reprisPar.trim(),
			date,
			heure,
			gardQ,
			gardT,
			frais,
			encaissement,
			moyenPaie: Number(moyenPaie),
			caissier: Number(caissier),
			payeur: Number(payeur),
			montant,
		})

	return (
		<CustomModal
			title="Livrer / Restituer"
			subtitle={
				dep.currentParc && dep.currentParc.label
					? `Véhicule au parc : ${dep.currentParc.label}`
					: "Véhicule dans un de nos parcs"
			}
			onClose={onClose}
			buttons={[
				{ title: "Fermer", styleName: "secondary", onClick: onClose },
				mode === "restituer"
					? {
							title: "Restituer",
							styleName: "primary",
							onClick: restituer,
							disabled: !valide,
						}
					: {
							title: "Créer la livraison",
							styleName: "primary",
							onClick: onLivrer,
							disabled: !peutLivrer,
						},
			]}
		>
			<div style={{ ...ms.pills, marginBottom: 16 }}>
				{[
					["restituer", "Restituer au client"],
					["livrer", "Livrer"],
				].map(([v, libelle]) => (
					<button
						key={v}
						type="button"
						style={ms.pill(mode === v)}
						onClick={() => setMode(v)}
					>
						{libelle}
					</button>
				))}
			</div>

			{mode === "restituer" && messageSuite && (
				<div style={ms.suite} role="note">
					{messageSuite}
				</div>
			)}

			{mode === "livrer" ? (
				<div style={{ fontSize: 13, color: COLORS.textMuted }}>
					Vers une adresse, un garage ou un autre de nos parcs : la
					livraison est créée pour le dispatch (plusieurs étapes =
					plusieurs livraisons).
					{!peutLivrer && (
						<div style={{ color: COLORS.danger, marginTop: 8 }}>
							Enregistrer la fiche avant de créer la livraison.
						</div>
					)}
				</div>
			) : (
				<div style={gs.fieldGroup}>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Repris par</label>
						<input
							style={gs.input}
							value={reprisPar}
							autoFocus
							onChange={(e) => setReprisPar(e.target.value)}
						/>
					</div>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Date et heure</label>
						<div style={ms.ligne}>
							<input
								type="date"
								style={gs.input}
								value={date}
								onChange={(e) =>
									changerQuand(e.target.value, heure)
								}
							/>
							<input
								type="time"
								style={gs.input}
								value={heure}
								onChange={(e) =>
									changerQuand(date, e.target.value)
								}
							/>
						</div>
					</div>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Gardiennage</label>
						<div>
							<div style={ms.ligne}>
								<input
									type="number"
									min={0}
									style={{ ...gs.input, width: 80 }}
									aria-label="Jours de gardiennage"
									value={gardQ}
									onChange={(e) => setGardQ(e.target.value)}
								/>
								<span>j ×</span>
								<input
									type="number"
									min={0}
									style={{ ...gs.input, width: 100 }}
									aria-label="Tarif par jour"
									value={gardT}
									onChange={(e) => setGardT(e.target.value)}
								/>
								<span>CHF/j = {chf(gardTot)}</span>
							</div>
							{periodes.map((p, i) => (
								<div key={i} style={ms.periode}>
									Au parc{p.parc ? ` ${p.parc}` : ""} du{" "}
									{p.debut.format("DD.MM.YYYY HH:mm")} au{" "}
									{p.fin.format("DD.MM.YYYY HH:mm")}
									{/* Revue du lot 9 (🟡L9-2) : plusieurs séjours =
									    dates seules, le total en jours (cumul des
									    heures). */}
									{periodes.length > 1
										? ""
										: ` → ${p.jours} j`}
								</div>
							))}
							{periodes.length > 1 && (
								<div style={{ ...ms.periode, fontWeight: 600 }}>
									Total : {joursCalcules(date, heure)} j
								</div>
							)}
							<div style={ms.aide}>
								{periodes.length > 1
									? "Jours comptés sur le cumul des heures au parc. "
									: ""}
								Calculé jusqu'à la date de restitution (dès la
								13ᵉ heure au parc, jours arrondis au supérieur).
							</div>
						</div>
					</div>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Frais hors heures</label>
						<div>
							<input
								type="number"
								min={0}
								style={{ ...gs.input, width: 120 }}
								aria-label="Frais de restitution hors heures"
								value={frais}
								onChange={(e) => setFrais(e.target.value)}
							/>
							<div style={ms.aide}>
								{configFrais && configFrais.montant > 0
									? `${chf(configFrais.montant)} avant ${configFrais.debut}, après ${configFrais.fin}${configFrais.weekend ? " et le week-end" : ""}`
									: "Désactivés dans la configuration"}
							</div>
						</div>
					</div>
					<div style={gs.fieldRow}>
						<label style={gs.label}>Encaissement</label>
						<div style={ms.pills}>
							{ENCAISSEMENTS.map(([v, libelle]) => (
								<button
									key={v}
									type="button"
									style={ms.pill(encaissement === v)}
									onClick={() => setEncaissement(v)}
								>
									{libelle}
								</button>
							))}
						</div>
					</div>
					{encaissement === "paye" && montant > 0 && (
						<>
							<div style={gs.fieldRow}>
								<label style={gs.label}>Moyen</label>
								<select
									style={gs.input}
									aria-label="Moyen de paiement"
									value={moyenPaie}
									onChange={(e) =>
										setMoyenPaie(e.target.value)
									}
								>
									<option value={0}>-</option>
									{filtrerMoyensPaiement(moyenPaiements, {
										module: "dep",
										valeurCourante: moyenPaie,
									}).map((mdp) => (
										<option key={mdp.id} value={mdp.id}>
											{mdp.nom}
										</option>
									))}
								</select>
							</div>
							<div style={gs.fieldRow}>
								<label style={gs.label}>Payé par</label>
								<select
									style={gs.input}
									aria-label="Payé par"
									value={payeur}
									onChange={(e) => setPayeur(e.target.value)}
								>
									{PAYEURS_RESTITUTION.map(([v, libelle]) => (
										<option key={v} value={v}>
											{libelle}
										</option>
									))}
								</select>
							</div>
							<div style={gs.fieldRow}>
								<label style={gs.label}>Caissier</label>
								<select
									style={gs.input}
									aria-label="Caissier"
									value={caissier}
									onChange={(e) =>
										setCaissier(e.target.value)
									}
								>
									<option value={0}>-</option>
									{filterEmployesActifs(
										employes,
										caissier,
									).map((e) => (
										<option key={e.id} value={e.id}>
											{e.nom}
										</option>
									))}
								</select>
							</div>
						</>
					)}
					<div style={ms.total}>
						{encaissement === "paye"
							? montant > 0
								? `Ligne « Restitution » de ${chf(montant)} (reste à payer), quittance après l'enregistrement`
								: "Rien à encaisser (reste à payer nul)"
							: encaissement === "rien"
								? "Aucune ligne créée"
								: "Aucune ligne créée (facture, assistance…)"}
					</div>
				</div>
			)}
		</CustomModal>
	)
}

export default DepLivrerRestituerModalV2
